'use client';

import type { ReactNode } from 'react';
import { Circle } from 'lucide-react';
import { cn } from '@/shared/lib/cn';

export interface PromoChip {
  icon?: ReactNode;
  label: string;
}

interface PromoBenefitsProps {
  /** Hero icon (lucide element) — sized/coloured by this component. */
  icon: ReactNode;
  title: string;
  subtitle?: string | null;
  /** Optional context chips (e.g. region / measurement system). */
  chips?: PromoChip[];
  /** Checkmark benefit bullets. */
  bullets?: string[];
  className?: string;
}

/**
 * Server-driven benefits/promo card for the empty feature tabs (Estimate,
 * Album/Blueprint). Hero icon + title + subtitle + optional context chips +
 * a checkmark bullet list. All copy is owned by the API and rendered verbatim
 * (API-driven messages) — this component owns only the static chrome (icon,
 * layout, checkmarks). Cross-platform parity with the Apple/Android intros.
 */
export function PromoBenefits({
  icon,
  title,
  subtitle,
  chips,
  bullets,
  className,
}: PromoBenefitsProps) {
  return (
    <div className={cn('px-1 pt-2', className)}>
      <div className="text-primary [&>svg]:h-8 [&>svg]:w-8 [&>svg]:stroke-[1.5]">
        {icon}
      </div>

      <h2 className="mt-2 text-card-title text-foreground">{title}</h2>

      {subtitle && (
        <p className="mt-1 text-card-body text-muted-foreground">{subtitle}</p>
      )}

      {chips && chips.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {chips.map((chip, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-card-meta text-muted-foreground [&>svg]:h-3.5 [&>svg]:w-3.5"
            >
              {chip.icon}
              {chip.label}
            </span>
          ))}
        </div>
      )}

      {bullets && bullets.length > 0 && (
        <ul className="mt-4 flex flex-col gap-3">
          {bullets.map((bullet, i) => (
            <li
              key={i}
              className="flex items-start gap-2.5 text-card-body text-muted-foreground"
            >
              <Circle
                className="mt-1.5 h-1.5 w-1.5 shrink-0 fill-muted-foreground text-muted-foreground"
                strokeWidth={0}
              />
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
